import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  Clock,
  ShieldCheck,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { classNames, fmtDateIST } from '@/lib/format';
import { FINAL_CALL_LABELS } from '@/lib/reportConstants';
import { finalCallTone } from '@/lib/clientReportHelpers';
import { GENERIC_EVAL_FAILURE_MSG } from '@/lib/evalErrors';

export function MagazineReportSidebar({ toc, active, link }) {
  return (
    <aside className="lg:sticky lg:top-6 lg:self-start space-y-4" data-testid="demo-sidebar">
      <nav className="rounded-2xl border bg-card p-3.5" data-testid="demo-toc">
        <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.055em] text-muted-foreground">Sections</p>
        <ol className="space-y-0.5">
          {toc.map((t, i) => (
            <li key={t.id}>
              <a
                href={`#${t.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(t.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                data-testid={`demo-toc-${t.id}`}
                className={classNames(
                  'flex w-full items-start gap-2 rounded-xl px-2 py-2 text-left text-[13.5px] transition-colors',
                  active === t.id ? 'bg-primary/10 text-primary' : 'hover:bg-secondary text-foreground/80',
                )}
              >
                <span className={classNames(
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-bold leading-none tabular-nums',
                  active === t.id ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground',
                )}>{i + 1}</span>
                <span>
                  <span className="block font-semibold leading-tight">{t.label}</span>
                  {t.tagline && <span className="mt-0.5 block text-[11.5px] text-muted-foreground leading-snug">{t.tagline}</span>}
                </span>
              </a>
            </li>
          ))}
        </ol>
        <div className="mt-2.5 flex items-center gap-1.5 border-t px-2 pt-3 text-[12.5px] text-muted-foreground">
          <Clock className="h-3.5 w-3.5" />
          <span>{link.expiry_days != null ? `Link expires in ${link.expiry_days} days` : link.expires_at ? `Expires ${fmtDateIST(link.expires_at, { month: 'short', day: 'numeric', year: 'numeric' })}` : 'No expiry set'}</span>
        </div>
      </nav>

      <div className="rounded-xl border bg-secondary/40 p-4 text-[12.5px] leading-5 text-foreground/80">
        <ShieldCheck className="mb-1 inline h-3.5 w-3.5 text-primary" /> Confidential. Each access is auditable. Please do not forward.
      </div>
    </aside>
  );
}

export function Module({ id, eyebrow, title, tagline, sidePill, icon: Icon, children, inlineTitle = false }) {
  return (
    <article id={id} data-section className="scroll-mt-6 rounded-2xl border bg-card p-5 shadow-sm">
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {inlineTitle ? (
            <div className="flex flex-wrap items-baseline gap-2.5">
              <p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-primary">
                {Icon && <Icon className="h-3 w-3" />} {eyebrow}
              </p>
              <h2 className="text-[18px] font-bold tracking-tight text-foreground">{title}</h2>
            </div>
          ) : (
            <>
              <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-primary">
                {Icon && <Icon className="h-3 w-3" />} {eyebrow}
              </p>
              <h2 className="mt-0.5 text-[24px] font-semibold tracking-tight">{title}</h2>
            </>
          )}
          {tagline && <p className="mt-1 text-[13px] italic text-muted-foreground">{tagline}</p>}
        </div>
        {sidePill}
      </header>
      <div className="mt-4 space-y-3">{children}</div>
    </article>
  );
}

export function RecommendationModule({ recommendation, hrFinalCall, onRegenerate }) {
  const rec = recommendation || {};
  const finalCall = rec.final_call || hrFinalCall || null;
  const finalCallPending = !finalCall;
  const finalCallLabel = finalCallPending
    ? 'Pending'
    : (rec.final_call_label || (finalCall ? FINAL_CALL_LABELS[finalCall] : null));
  const tone = finalCallPending ? 'empty' : finalCallTone(finalCall);
  const status = rec.status || (rec.grounded_in_data ? 'completed' : 'awaiting_evidence');
  const lp = rec.learning_plan || {};
  const phases = [
    { key: 'days_1_30', title: 'Days 1–30 · Foundation', items: lp.days_1_30 || [] },
    { key: 'days_31_60', title: 'Days 31–60 · Applied Practice', items: lp.days_31_60 || [] },
    { key: 'days_61_90', title: 'Days 61–90 · Independent Delivery', items: lp.days_61_90 || [] },
  ];

  // Client-side safety net when backend timeout/poll misses — mirrors Video Bot eval UX.
  const [clientTimedOut, setClientTimedOut] = useState(false);
  useEffect(() => {
    setClientTimedOut(false);
    if (status !== 'pending') return undefined;
    const t = setTimeout(() => setClientTimedOut(true), 100_000);
    return () => clearTimeout(t);
  }, [status, rec.updated_at, rec.error_text]);

  const aiAwaiting = status === 'awaiting_evidence';
  const aiAwaitingFinalCall = status === 'awaiting_final_call' || (finalCallPending && status === 'not_generated');
  const aiNotGenerated = status === 'not_generated' && !finalCallPending;
  const aiFailed = status === 'failed' || (status === 'pending' && clientTimedOut);
  const aiPending = status === 'pending' && !aiFailed;
  const aiCompleted = status === 'completed';
  const failMessage = GENERIC_EVAL_FAILURE_MSG;
  const awaitingMessage = rec.error_text
    || 'Not enough stage scores yet. Complete Video Bot, IQ, Work, Panel, or HR ratings, then click Generate.';
  const canGenerate = Boolean(onRegenerate) && !finalCallPending && !aiPending && (aiNotGenerated || aiAwaiting || aiFailed || aiCompleted);

  const bannerSubtitle = finalCallPending
    ? 'Set Final call on the Offer tab, then click Generate for the AI body.'
    : (aiCompleted && rec.subtitle
      ? rec.subtitle
      : aiPending
        ? 'AI narrative generating below…'
        : aiAwaiting
          ? 'Waiting for stage scores before synthesizing the AI body.'
          : aiNotGenerated
            ? 'Click Generate to synthesize the AI recommendation from available scores.'
            : aiFailed
              ? 'AI recommendation could not be generated — see details below.'
              : 'AI subtitle will appear when generation completes.');

  return (
    <Module id="recommendation" eyebrow="Final call" title="Recommendation" icon={Sparkles} inlineTitle>
      <p className="mb-3 text-[11.5px] text-muted-foreground" data-testid="recommendation-ai-attribution">
        Body text is AI-synthesized from stage scores after you click Generate (Final Call must be saved first).
        The Final call banner title is set by HR on the Offer tab.
      </p>

      {/* Zone A — HR Final call (manual input) */}
      <div
        className={classNames(
          'mb-4 flex items-center gap-3.5 rounded-xl border px-[18px] py-[18px]',
          tone === 'good' && 'border-emerald-200 bg-emerald-50',
          tone === 'neutral' && 'border-amber-200 bg-amber-50',
          tone === 'bad' && 'border-rose-200 bg-rose-50',
          tone === 'empty' && 'border-border bg-secondary/40',
        )}
        data-testid="recommendation-verdict-banner"
      >
        <div
          className={classNames(
            'flex h-10 w-10 flex-none items-center justify-center rounded-full text-base font-extrabold text-white',
            tone === 'good' && 'bg-emerald-600',
            tone === 'neutral' && 'bg-amber-600',
            tone === 'bad' && 'bg-rose-600',
            tone === 'empty' && 'bg-slate-400',
          )}
        >
          {tone === 'good' ? '\u2713' : tone === 'empty' ? '?' : '!'}
        </div>
        <div className="min-w-0">
          <div
            className={classNames(
              'text-[16px] font-bold',
              tone === 'good' && 'text-emerald-800',
              tone === 'neutral' && 'text-amber-900',
              tone === 'bad' && 'text-rose-900',
              tone === 'empty' && 'text-foreground',
            )}
            data-testid={finalCallPending ? 'recommendation-final-call-pending' : 'recommendation-final-call-label'}
          >
            {finalCallPending ? 'Final call · Pending' : finalCallLabel}
          </div>
          <div
            className={classNames(
              'mt-0.5 text-[13px]',
              tone === 'good' && 'text-emerald-700',
              tone === 'neutral' && 'text-amber-800',
              tone === 'bad' && 'text-rose-800',
              tone === 'empty' && 'text-muted-foreground',
            )}
            data-testid="recommendation-subtitle"
          >
            {bannerSubtitle}
          </div>
        </div>
      </div>

      {/* Zone B — AI synthesis */}
      {(aiAwaitingFinalCall || status === 'awaiting_final_call') && (
        <div
          className="mb-4 rounded-lg border border-dashed bg-secondary/30 p-3 text-[13px] text-muted-foreground"
          data-testid="recommendation-ai-awaiting-final-call"
        >
          <p className="m-0 font-medium text-foreground/80">Final Call required</p>
          <p className="mt-1 mb-0">
            {rec.error_text || 'Set and save HR Final Call on the Offer tab before generating the recommendation.'}
          </p>
        </div>
      )}

      {aiAwaiting && !finalCallPending && (
        <div
          className="mb-4 rounded-lg border border-dashed bg-secondary/30 p-3 text-[13px] text-muted-foreground"
          data-testid="recommendation-ai-awaiting-evidence"
        >
          <p className="m-0 font-medium text-foreground/80">Waiting for stage scores</p>
          <p className="mt-1 mb-0">{awaitingMessage}</p>
        </div>
      )}

      {aiNotGenerated && (
        <div
          className="mb-4 rounded-lg border border-dashed bg-secondary/30 p-3 text-[13px] text-muted-foreground"
          data-testid="recommendation-ai-not-generated"
        >
          <p className="m-0 font-medium text-foreground/80">Recommendation not generated yet</p>
          <p className="mt-1 mb-0">
            {rec.error_text || 'Click Generate to synthesize the staff recommendation from available stage scores.'}
          </p>
          {onRegenerate && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={onRegenerate}
              data-testid="recommendation-generate"
            >
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Generate
            </Button>
          )}
        </div>
      )}

      {aiPending && (
        <div
          className="mb-4 flex items-center gap-2 rounded-lg border border-dashed bg-secondary/30 p-3 text-[13px] text-muted-foreground"
          data-testid="recommendation-ai-pending"
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span>
            AI recommendation in progress — synthesizing grounded rationale, watch-outs, and 90-day learning plan.
          </span>
        </div>
      )}

      {aiFailed && (
        <div
          className="mb-4 flex flex-wrap items-center gap-2"
          data-testid="recommendation-ai-failed"
        >
          <p className="text-[13px] text-rose-600">{failMessage}</p>
          {canGenerate && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs text-rose-700"
              onClick={onRegenerate}
              data-testid="recommendation-regenerate"
            >
              <RefreshCw className="mr-1 h-3 w-3" /> Retry
            </Button>
          )}
        </div>
      )}

      {aiCompleted && (
        <>
          {canGenerate && (
            <div className="mb-3 flex justify-end">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-[12px] text-muted-foreground"
                onClick={onRegenerate}
                data-testid="recommendation-regenerate"
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Regenerate from latest scores
              </Button>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="recommendation-cards">
            <div className="rounded-xl border bg-secondary/40 px-[18px] py-4">
              <h4 className="mb-2 text-[13px] font-bold tracking-wide">Grounded in the data</h4>
              <p className="m-0 text-[13px] leading-relaxed text-foreground/80" data-testid="recommendation-grounded">
                {rec.grounded_in_data}
              </p>
            </div>
            <div className="rounded-xl border bg-secondary/40 px-[18px] py-4">
              <h4 className="mb-2 text-[13px] font-bold tracking-wide">Watch-outs to plan around</h4>
              <p className="m-0 text-[13px] leading-relaxed text-foreground/80" data-testid="recommendation-watch-outs">
                {rec.watch_outs}
              </p>
            </div>
          </div>

          <div className="mt-5 border-t pt-[18px]" data-testid="recommendation-learning-plan">
            <h4 className="m-0 mb-1 text-[14px] font-bold">Candidate Learning Plan — First 90 Days</h4>
            <p className="mb-3.5 mt-0 text-[12.5px] leading-relaxed text-muted-foreground">
              {rec.learning_plan_intro}
            </p>
            <div className="grid grid-cols-1 gap-3.5 md:grid-cols-3">
              {phases.map((phase) => (
                <div key={phase.key} className="rounded-xl border bg-secondary/40 px-4 py-3.5" data-testid={`recommendation-phase-${phase.key}`}>
                  <div className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.05em] text-primary">
                    {phase.title}
                  </div>
                  <ul className="m-0 flex list-none flex-col gap-2 p-0">
                    {phase.items.map((item) => (
                      <li key={item} className="flex gap-1.5 text-[12.5px] leading-relaxed text-foreground/80">
                        <span className="flex-none font-bold text-primary">{'\u2022'}</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {(rec.next_steps || []).length > 0 && (
            <div className="mt-5" data-testid="recommendation-next-steps">
              <h4 className="mb-2 text-[13px] font-bold">Suggested next steps</h4>
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {rec.next_steps.map((step) => (
                  <li key={step} className="flex gap-2 text-[13px] leading-relaxed text-foreground/80">
                    <span className="flex-none font-bold text-primary">{'\u2192'}</span>
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Module>
  );
}

export function McqItemRow({ item, idx }) {
  return (
    <div className="rounded-lg border bg-card p-3" data-testid={`demo-mcq-q-${idx}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Q{idx + 1} {'\u00b7'} {item.topic} {'\u00b7'} {item.difficulty}
          </p>
          <p className="text-sm font-medium leading-snug">{item.question_text}</p>
        </div>
        {item.is_correct ? (
          <Badge variant="outline" className="shrink-0 border-emerald-300 bg-emerald-50 text-[10px] text-emerald-800">
            <CheckCircle2 className="mr-1 h-3 w-3" /> Correct
          </Badge>
        ) : (
          <Badge variant="outline" className="shrink-0 border-rose-300 bg-rose-50 text-[10px] text-rose-800">
            <AlertTriangle className="mr-1 h-3 w-3" /> Incorrect
          </Badge>
        )}
      </div>
      <ul className="mt-2 space-y-1">
        {item.options.map((opt, i) => {
          const isSelected = i === item.selected_index;
          const isCorrect  = i === item.correct_index;
          const tone =
            isSelected && isCorrect ? 'border-emerald-300 bg-emerald-50 text-emerald-900' :
            isSelected && !isCorrect ? 'border-rose-300 bg-rose-50 text-rose-900' :
            isCorrect ? 'border-emerald-200 bg-emerald-50/40 text-emerald-900' :
            'border-border bg-card text-foreground/80';
          return (
            <li key={i} className={`flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs ${tone}`}>
              <span className="font-mono text-[10px]">{String.fromCharCode(65 + i)}</span>
              <span className="flex-1">{opt}</span>
              {isSelected && <span className="text-[10px] uppercase tracking-wider">Selected</span>}
              {isCorrect && !isSelected && <span className="text-[10px] uppercase tracking-wider text-emerald-700">Correct</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
